import { useState, useEffect } from 'react';
import { saveConsentPreferences, hasConsent, getConsentPreferences, type ConsentPreferences } from '../firebase';
import './CookieConsent.css';

/**
 * Global Privacy Consent Banner
 * Complies with: GDPR (EU), UK GDPR, CCPA/CPRA (California), LGPD (Brazil), PIPEDA (Canada)
 * 
 * Key features:
 * - Opt-in by default (GDPR requirement)
 * - Granular consent options
 * - "Do Not Sell/Share" option (CCPA requirement)
 * - Consent withdrawal option
 * - Persistent storage with timestamp
 */
export function CookieConsent() {
    const [isVisible, setIsVisible] = useState(false);
    const [showPreferences, setShowPreferences] = useState(false);
    const [preferences, setPreferences] = useState({
        // GDPR: non-essential consent is opt-IN, not opt-out. Both
        // categories start unchecked so "Save Preferences" only grants
        // consent the user has explicitly toggled on.
        analytics: false,
        marketing: false,
    });

    useEffect(() => {
        // Show banner if no consent given yet
        if (!hasConsent()) {
            // Delay slightly for better UX
            const timer = setTimeout(() => setIsVisible(true), 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleAcceptAll = () => {
        const consent: ConsentPreferences = {
            analytics: true,
            marketing: true,
            timestamp: Date.now(),
        };
        saveConsentPreferences(consent);
        setIsVisible(false);
    };

    const handleSavePreferences = () => {
        const consent: ConsentPreferences = {
            ...preferences,
            timestamp: Date.now(),
        };
        saveConsentPreferences(consent);
        setIsVisible(false);
    };

    const handleRejectAll = () => {
        const consent: ConsentPreferences = {
            analytics: false,
            marketing: false,
            timestamp: Date.now(),
        };
        saveConsentPreferences(consent);
        setIsVisible(false);
    };

    if (!isVisible) return null;

    return (
        <div className="cookie-banner" role="region" aria-label="Cookie consent">
            <div className="cookie-content">
                {!showPreferences ? (
                    <>
                        <div className="cookie-text">
                            <div className="cookie-icon" aria-hidden="true">
                                {/* 20px inline lock icon — replaced the 🔒
                                    emoji which renders inconsistently
                                    across platforms. */}
                                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="3" y="11" width="18" height="10" rx="2" ry="2" />
                                    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                                </svg>
                            </div>
                            <div>
                                <p className="cookie-title">Your Privacy Matters</p>
                                <p className="cookie-description">
                                    We use essential cookies to run the site. Analytics and marketing
                                    cookies stay <strong>off by default</strong> until you turn them on.
                                    Choose &ldquo;Accept All&rdquo;, or pick categories under
                                    &ldquo;Manage Preferences&rdquo;. See our{' '}
                                    <a href="/privacy" className="cookie-link">Privacy Policy</a>.
                                    {' '}California residents:{' '}
                                    <a href="/privacy#ccpa" className="cookie-link">Do Not Sell or Share My Personal Information</a>.
                                </p>
                            </div>
                        </div>
                        <div className="cookie-actions">
                            <button className="cookie-btn cookie-btn-secondary" onClick={() => setShowPreferences(true)}>
                                Manage Preferences
                            </button>
                            <button className="cookie-btn cookie-btn-secondary" onClick={handleRejectAll}>
                                Reject All
                            </button>
                            <button className="cookie-btn cookie-btn-primary" onClick={handleAcceptAll}>
                                Accept All
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="cookie-preferences">
                            <p className="cookie-title">Privacy Preferences</p>
                            <p className="cookie-subtitle">
                                Manage how we use data to personalize your experience.
                                You can change these settings at any time.
                            </p>
                            <div className="cookie-option">
                                <label>
                                    <input type="checkbox" checked disabled />
                                    <span>Essential</span>
                                    <span className="cookie-badge">Always On</span>
                                </label>
                                <span className="cookie-option-desc">
                                    Required for security, authentication, and core functionality. Cannot be disabled.
                                </span>
                            </div>
                            <div className="cookie-option">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={preferences.analytics}
                                        onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                                    />
                                    <span>Analytics & Performance</span>
                                </label>
                                <span className="cookie-option-desc">
                                    Help us understand how visitors use our site to improve performance and content.
                                </span>
                            </div>
                            <div className="cookie-option">
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={preferences.marketing}
                                        onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                                    />
                                    <span>Marketing & Personalization</span>
                                </label>
                                <span className="cookie-option-desc">
                                    Personalized ads and content based on your browsing.
                                    <strong> Disabling this also opts you out of "Sale/Share" under CCPA.</strong>
                                </span>
                            </div>
                        </div>
                        <div className="cookie-actions">
                            <button className="cookie-btn cookie-btn-secondary" onClick={() => setShowPreferences(false)}>
                                Back
                            </button>
                            <button className="cookie-btn cookie-btn-primary" onClick={handleSavePreferences}>
                                Save Preferences
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

/**
 * Footer Privacy Link Component
 * Use this in your footer to allow users to reopen preferences
 */
export function PrivacySettingsLink() {
    const [, setTrigger] = useState(0);

    const handleClick = () => {
        // Clear consent to show banner again
        localStorage.removeItem('cookie_consent');
        setTrigger(t => t + 1);
        window.location.reload();
    };

    const currentConsent = getConsentPreferences();

    return (
        <button
            className="privacy-settings-link"
            onClick={handleClick}
            title="Manage your privacy preferences"
        >
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ marginRight: 6, verticalAlign: 'text-bottom' }}>
                <rect x="3" y="11" width="18" height="10" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Privacy Settings
            {currentConsent && (
                <span className="privacy-status">
                    {currentConsent.analytics ? '✓' : '○'}
                </span>
            )}
        </button>
    );
}
